import type { Post } from "@repo/fika-shared/src/loader/post-schema.js"
import { createMemo, createSignal, Show } from "solid-js"
import { useAppContext } from "@/context/app"
import { Box } from "@/designSystem/components/box"
import { Button } from "@/designSystem/components/button"
import { Form } from "@/designSystem/components/form"
import { Switch } from "@/designSystem/components/switch"
import { Text } from "@/designSystem/components/text"
import { TextField } from "@/designSystem/components/textField"
import { Well } from "@/designSystem/components/well"
import { MainLayout } from "@/designSystem/layouts/mainLayout"
import { Navigation } from "@/designSystem/patterns/navigation"
import { Section } from "@/designSystem/patterns/section"
import { useI18nContext } from "@/i18n/i18n-solid"
import { getTitle } from "@/lib/getTitle"
import { createBlogRouter } from "@/routes/posts-router"
import usePost from "./use-post"

export function PostSettings() {
  const post = usePost()

  return <Show when={post()}>{post => <SafePostSettings post={post()} />}</Show>
}

function SafePostSettings(props: { post: Post }) {
  const { LL } = useI18nContext()
  const { db } = useAppContext()
  const { replicache } = useAppContext()
  const title = createMemo(() => getTitle(props.post.title))
  const defaultMetaTitle = createMemo(() => props.post.meta_title || title())
  const defaultMetaDescription = createMemo(
    () => props.post.meta_description || props.post.description,
  )
  const [formPinned, setFormPinned] = createSignal(!!props.post.pinned_at)
  const [formTitle, setFormTitle] = createSignal(
    defaultMetaTitle() || undefined,
  )
  const [formDescription, setFormDescription] = createSignal(
    defaultMetaDescription() || undefined,
  )
  const [formCanonicalUrl, setFormCanonicalUrl] = createSignal(
    props.post.canonical_url || undefined,
  )
  const seoMetadataDirty = createMemo(
    () =>
      formTitle() !== defaultMetaTitle() ||
      formDescription() !== defaultMetaDescription(),
  )

  const canonicalUrlDirty = createMemo(
    () =>
      formCanonicalUrl() !== props.post.canonical_url && !!formCanonicalUrl(),
  )

  const pinnedDirty = createMemo(() => formPinned() !== !!props.post.pinned_at)
  const { paths } = createBlogRouter()
  const archived = createMemo(() => !!props.post.archived_at)
  const alreadyPublishedOrArchived = createMemo(() =>
    db.post
      .filterBy({ root_id: props.post.id })
      .some(post => post.published_at || post.archived_at),
  )

  const buttons = createMemo(() => {
    return [
      {
        label: LL().common.blog(),
        href: paths().url,
      } as const,
      {
        label: title(),
        href: paths().post(props.post).url,
      } as const,
    ]
  })

  function onSubmitSeoMetadata() {
    replicache.mutate.updatePost({
      id: props.post.id,
      meta_title: formTitle() || null,
      meta_description: formDescription() || null,
    })
  }

  function onSubmitCanonicalUrl() {
    replicache.mutate.updatePost({
      id: props.post.id,
      canonical_url: formCanonicalUrl() || null,
    })
  }

  function onSubmitPin() {
    replicache.mutate.updatePost({
      id: props.post.id,
      pinned_at: props.post.pinned_at ? null : new Date().toISOString(),
    })
  }

  return (
    <MainLayout
      header={<Navigation hue="amber" buttons={buttons()} title="settings" />}
    >
      <Box gap="24">
        <Form
          title="feature post"
          description="feature this post on your blog's home"
          hue="amber"
          iconName="star"
          onSubmit={onSubmitPin}
        >
          <Switch
            label="feature post"
            hue="amber"
            checked={formPinned()}
            onChange={setFormPinned}
          />
          <Form.Save hue="amber" iconName="star" disabled={!pinnedDirty()} />
        </Form>
        <Form
          title="SEO metadata"
          description="the title and description of the post as it will appear in search results"
          hue="amber"
          iconName="captions"
          onSubmit={onSubmitSeoMetadata}
        >
          <TextField value={formTitle()} onChange={setFormTitle}>
            <TextField.Label>meta title</TextField.Label>
            <TextField.TextArea
              rows="1"
              autoResize
              placeholder="your blog title"
            />
          </TextField>
          <TextField value={formDescription()} onChange={setFormDescription}>
            <TextField.Label>meta description</TextField.Label>
            <TextField.TextArea
              rows="1"
              autoResize
              placeholder="your blog description"
            />
          </TextField>
          <Form.Save
            hue="amber"
            iconName="captions"
            disabled={!seoMetadataDirty()}
          />
        </Form>
        <Form
          title="Canonical URL"
          description="If the original source of the post is another website, you can specify the canonical URL here to prevent duplicate content issues."
          hue="amber"
          iconName="link"
          onSubmit={onSubmitCanonicalUrl}
        >
          <TextField value={formCanonicalUrl()} onChange={setFormCanonicalUrl}>
            <TextField.Label>canonical url</TextField.Label>
            <TextField.Input
              type="text"
              placeholder="https://example.com/post/123"
            />
          </TextField>
          <Form.Save
            hue="amber"
            iconName="link"
            disabled={!canonicalUrlDirty()}
          />
        </Form>
        <Show when={alreadyPublishedOrArchived()}>
          <Section
            title={archived() ? "unarchive post" : "archive post"}
            description={
              archived()
                ? "show this post on your blog."
                : "hide this post from your blog."
            }
            hue="orange"
            iconName="archive"
          >
            <Well hue="orange">
              <Box
                flexDirection="row"
                justifyContent="flex-end"
                alignItems="center"
                gap="12"
              >
                <Button
                  hue={archived() ? "emerald" : "orange"}
                  iconName="archive"
                  onClick={() => {
                    replicache.mutate.archivePost({
                      id: props.post.id,
                      archived_at: archived() ? null : new Date().toISOString(),
                    })
                  }}
                >
                  {archived() ? "unarchive" : "archive"}
                </Button>
              </Box>
            </Well>
          </Section>
        </Show>
        <Section
          title="delete post"
          description="remove this post entirely."
          hue="rose"
          iconName="trash-2"
        >
          <Well hue="rose">
            <Box
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              gap="12"
            >
              <Text color="rose800">this action cannot be undone.</Text>
              <Button
                hue="rose"
                iconName="trash-2"
                onClick={() => {
                  replicache.mutate.deletePost(props.post.id)
                }}
              >
                delete
              </Button>
            </Box>
          </Well>
        </Section>
      </Box>
    </MainLayout>
  )
}
